import {
  normalizeWorkerRunDateInputValue,
  renderWorkerInputField,
} from "#src/batteries-included-components/Drawers/Workflows/WorkflowConfigurationDrawer/Utils/WorkflowConfigurationRunWorker.helpers";
import { KeyValueTags } from "#src/batteries-included-components/KeyValueTags/KeyValueTags";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import {
  useCreateOneWorkerRun,
  useGenerateWorkerDropdownOptions,
  useListWorkers,
  useListWorkerVersions,
} from "#src/components/hooks/adapters/useWorkers";
import { useRunWorkerDrawer } from "#src/contexts/RunWorkerDrawer";
import { useWorkerRunQueue } from "#src/hooks/useWorkerRunQueue";
import { WorkerRunDetailRoutePath } from "#src/routes/workers/runs/[runId]";
import { ExceptionUtils } from "#src/utils/exception";
import {
  Banner,
  Button,
  ButtonToggleGroup,
  Chip,
  ControlLabel,
  DataTable,
  Drawer,
  DropdownInputBare,
  EmptyState,
  Form,
  Icon,
  IconLoading,
  ListCard,
  Pill,
  Tooltip,
  useForm,
  useToast,
} from "@validereinc/common-components";
import {
  AttributeDataType,
  SortDirection,
  WorkerAdapter,
  WorkerRunStatus,
  WorkerVersionStatus,
  WorkerVersionType,
  type InputOutputSchemaPropertyConfigurationType,
  type WorkerRunType,
} from "@validereinc/domain";
import { FeatureFlags, PermissionsMap } from "@validereinc/domain-controllers";
import React, { useEffect, useMemo, useRef, useState } from "react";

type Tabs = "start_run" | "runs";

const getLatestVersionNumberFromVersions = (versions: WorkerVersionType[]) =>
  Math.max(...versions.map((v) => v.version));

/** Stable empty object for "no input schema" to avoid new references on every render. */
const EMPTY_INPUT_SCHEMA: Record<
  string,
  InputOutputSchemaPropertyConfigurationType
> = {};

/**
 * Builds the `inputs` payload for `POST /worker_runs` from the run form.
 */
const buildWorkerRunInputsRecord = (
  inputSchemaEntries: Array<
    [string, InputOutputSchemaPropertyConfigurationType]
  >,
  formValues: Record<string, unknown>
): Record<string, unknown> => {
  if (inputSchemaEntries.length === 0) return {};
  const out: Record<string, unknown> = {};
  for (const [key, def] of inputSchemaEntries) {
    const raw = formValues[key];
    switch (def.data_type) {
      case AttributeDataType.BOOLEAN:
      case "boolean":
        out[key] = Boolean(raw);
        break;
      case AttributeDataType.NUMBER:
      case AttributeDataType.INTEGER:
      case "number":
      case "integer":
        out[key] = raw === "" || raw == null ? undefined : Number(raw);
        break;
      case AttributeDataType.MULTI_PICK_LIST:
      case "multi-pick-list":
      case AttributeDataType.MULTI_LOOKUP:
      case "multi-lookup":
        out[key] = Array.isArray(raw) ? raw : [];
        break;
      case AttributeDataType.DATE_TIME:
      case "date-time":
      case AttributeDataType.DATE:
      case "date":
        out[key] = normalizeWorkerRunDateInputValue(raw, def);
        break;
      default:
        out[key] = raw ?? "";
    }
  }
  return out;
};

/**
 * Global Run Worker drawer. Rendered in layout; open/close state is controlled
 * via useRunWorkerDrawer (e.g. "Run Worker" button sets isOpen to true).
 * Only rendered when the user has the Custom Code feature flag and Worker Run write permission.
 */
export const RunWorkerDrawer = () => {
  const {
    isOpen,
    setIsOpen,
    selectedWorkerId,
    setSelectedWorkerId,
    selectedVersion,
    setSelectedVersion,
    formInputValues,
    setFormInputValues,
    resetSelection,
    startedWorkerRunsCount,
  } = useRunWorkerDrawer();
  const { toast } = useToast();

  const { data: workersData, isLoading: isLoadingWorkers } = useListWorkers(
    {},
    { enabled: isOpen }
  );
  const workers = workersData?.data ?? [];

  const workerOptions = useGenerateWorkerDropdownOptions(workers);

  const { queue, setQueue, runIds, runPollQueries, dismissableQueuedRunIds } =
    useWorkerRunQueue();

  const lastStatusByRunIdRef = useRef(
    new Map<string, WorkerRunType["status"]>()
  );

  const runPollStatusSignature = runPollQueries
    .map((query, index) => {
      const id = runIds[index] ?? "";
      return `${id}:${query.data?.status ?? ""}:${query.dataUpdatedAt}`;
    })
    .join("|");

  useEffect(() => {
    runIds.forEach((id, index) => {
      const run = runPollQueries[index]?.data;
      if (run == null) return;
      const workerName =
        queue.getAsArray().find((entry) => entry.id === id)?.workerName ??
        "Worker";
      const previousStatus = lastStatusByRunIdRef.current.get(id);
      if (
        previousStatus === WorkerRunStatus.STARTED &&
        run.status === WorkerRunStatus.COMPLETED
      ) {
        toast.push({
          intent: "success",
          description: `${workerName} (v${run.version}) completed successfully.`,
        });
      }
      if (
        previousStatus === WorkerRunStatus.STARTED &&
        run.status === WorkerRunStatus.FAILED
      ) {
        toast.push({
          intent: "error",
          description:
            run.error_message?.trim() ??
            `${workerName} (v${run.version}) failed.`,
        });
      }
      lastStatusByRunIdRef.current.set(id, run.status);
    });
  }, [queue, runIds, runPollStatusSignature, toast]);

  const createWorkerRunMutation = useCreateOneWorkerRun({ noAlerts: true });

  const listVersionsParams = useMemo(
    () =>
      selectedWorkerId
        ? {
            meta: { id: selectedWorkerId },
            page: 1,
            pageSize: 500,
            sortBy: "version" as const,
            sortDirection: SortDirection.DESCENDING,
            filters: { status: WorkerVersionStatus.ACTIVE },
          }
        : null,
    [selectedWorkerId]
  );
  const { data: versionsData, isLoading: isLoadingVersions } =
    useListWorkerVersions(
      listVersionsParams as Parameters<
        typeof WorkerAdapter.versions.getList
      >[0],
      { enabled: Boolean(selectedWorkerId) && isOpen }
    );
  const versions = versionsData?.data ?? [];
  const latestVersionNumber =
    versions.length > 0 ? getLatestVersionNumberFromVersions(versions) : null;
  const versionOptions = useMemo(
    () =>
      versions.map((v) => ({
        label: `v${v.version}${v.version === latestVersionNumber ? " (latest)" : ""}`,
        value: String(v.version),
      })),
    [versions, latestVersionNumber]
  );

  /**
   * Keep `selectedVersion` aligned with `selectedWorkerId`. Entry points may set
   * only the worker id; without this, a version from a previously selected worker
   * can remain and be sent to `createWorkerRun`. Prefer the latest active version
   * when missing or not in the current worker's list.
   */
  useEffect(() => {
    if (!selectedWorkerId || isLoadingVersions) return;

    if (versions.length === 0) {
      if (selectedVersion !== undefined) {
        setSelectedVersion(undefined);
      }
      return;
    }

    const activeVersionNumbers = new Set(versions.map((v) => v.version));
    const isSelectedVersionValidForWorker =
      selectedVersion !== undefined &&
      activeVersionNumbers.has(selectedVersion);

    if (!isSelectedVersionValidForWorker) {
      setSelectedVersion(getLatestVersionNumberFromVersions(versions));
    }
  }, [
    selectedWorkerId,
    isLoadingVersions,
    versions,
    selectedVersion,
    setSelectedVersion,
  ]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleWorkerChange = (val: unknown) => {
    const workerId = val == null || val === "" ? undefined : (val as string);
    setSelectedWorkerId(workerId);
    setSelectedVersion(undefined);
  };

  const noVersionsToChooseFrom =
    Boolean(selectedWorkerId) && !isLoadingVersions && versions.length === 0;

  const selectedVersionData = useMemo(
    () => versions.find((v) => v.version === selectedVersion),
    [versions, selectedVersion]
  );
  const inputSchema = useMemo(
    (): Record<string, InputOutputSchemaPropertyConfigurationType> =>
      selectedVersionData?.input_schema ?? EMPTY_INPUT_SCHEMA,
    [selectedVersionData?.input_schema]
  );
  const inputSchemaEntries = useMemo(
    () =>
      Object.entries(inputSchema).sort(
        ([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0)
      ),
    [inputSchema]
  );

  const runInputDefaultValues = useMemo((): Record<
    string,
    boolean | string | string[] | undefined
  > => {
    const entries: Array<[string, boolean | string | string[] | undefined]> =
      Object.entries(inputSchema).map(([key, def]) => {
        if (def.data_type === AttributeDataType.BOOLEAN) {
          return [key, false];
        }
        if (
          def.data_type === AttributeDataType.MULTI_PICK_LIST ||
          def.data_type === AttributeDataType.MULTI_LOOKUP
        ) {
          return [key, [] as string[]];
        }
        if (
          def.data_type === AttributeDataType.DATE_TIME ||
          def.data_type === AttributeDataType.DATE
        ) {
          return [key, undefined];
        }
        return [key, ""];
      });
    return Object.fromEntries(entries);
  }, [inputSchema]);

  const hasStoredValues = Object.keys(formInputValues).length > 0;
  const runInputForm = useForm({
    mode: "onChange",
    defaultValues: hasStoredValues ? formInputValues : runInputDefaultValues,
  });

  const { reset: resetRunInputForm } = runInputForm;

  /**
   * Only reset the form when the user intentionally changes the worker or
   * version — not on incidental re-renders or drawer open/close cycles.
   */
  const previousSelectionRef = useRef({
    workerId: selectedWorkerId,
    version: selectedVersion,
  });
  const isInitialMountRef = useRef(true);
  useEffect(() => {
    const prev = previousSelectionRef.current;
    const selectionChanged =
      prev.workerId !== selectedWorkerId || prev.version !== selectedVersion;

    previousSelectionRef.current = {
      workerId: selectedWorkerId,
      version: selectedVersion,
    };

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      if (hasStoredValues) return;
    }

    if (selectionChanged) {
      resetRunInputForm(runInputDefaultValues);
      setFormInputValues({});
    }
  }, [
    resetRunInputForm,
    runInputDefaultValues,
    selectedWorkerId,
    selectedVersion,
    setFormInputValues,
    hasStoredValues,
  ]);

  /** Sync form values to the store on every change so they survive unmounts. */
  useEffect(() => {
    const subscription = runInputForm.watch((values) => {
      setFormInputValues(values as Record<string, unknown>);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [runInputForm, setFormInputValues]);

  const canRunWorker =
    Boolean(selectedWorkerId) &&
    selectedVersion != null &&
    !noVersionsToChooseFrom &&
    (inputSchemaEntries.length === 0 || runInputForm.formState.isValid);

  const [activeTab, setActiveTab] = useState<Tabs>("start_run");

  const queueSize = queue.size();

  return (
    <IsFeatureAvailable
      featureFlagQuery={FeatureFlags.CustomCodeFeatureFlag}
      permissionQuery={PermissionsMap.WorkerRunWrite}
      fallbackChildren={null}
    >
      <Drawer
        isOpen={isOpen}
        onClose={handleClose}
        title="Run Worker"
        size="lg"
        mode="non-blocking"
        actionRow={
          activeTab === "start_run"
            ? [
                <Button
                  key="clear"
                  variant="outline"
                  onClick={resetSelection}
                >
                  Clear
                </Button>,
                <Button
                  key="run-worker"
                  variant="primary"
                  isLoading={createWorkerRunMutation.isLoading}
                  onClick={() => {
                    void (async () => {
                      if (!selectedWorkerId || selectedVersion == null) return;
                      if (inputSchemaEntries.length > 0) {
                        const isValid = await runInputForm.trigger();
                        if (!isValid) return;
                      }
                      try {
                        const inputs = buildWorkerRunInputsRecord(
                          inputSchemaEntries,
                          runInputForm.getValues()
                        );
                        const result =
                          await createWorkerRunMutation.mutateAsync({
                            data: {
                              worker_id: selectedWorkerId,
                              version: selectedVersion,
                              inputs,
                            },
                          });
                        const createdRun = result.data;
                        const workerName =
                          workers.find((w) => w.id === selectedWorkerId)
                            ?.name ?? "";
                        lastStatusByRunIdRef.current.set(
                          createdRun.id,
                          createdRun.status
                        );
                        setQueue((currentQueue) =>
                          currentQueue.insert({
                            id: createdRun.id,
                            workerName,
                            version: createdRun.version,
                            started_at: createdRun.started_at,
                            inputs: createdRun.inputs ?? inputs,
                            polledRun: createdRun,
                          })
                        );
                        toast.push({
                          intent: "info",
                          description: `${workerName} (v${createdRun.version}) run has been queued.`,
                        });
                        resetSelection();
                      } catch (error: unknown) {
                        ExceptionUtils.reportException(error, "error", {
                          sourceComponent: "RunWorkerDrawer",
                          hint: "createWorkerRun",
                        });
                        toast.push({
                          intent: "error",
                          description: "Failed to start worker run.",
                        });
                      }
                    })();
                  }}
                  disabled={!canRunWorker || createWorkerRunMutation.isLoading}
                >
                  Run Worker
                </Button>,
              ]
            : undefined
        }
      >
        <ButtonToggleGroup
          isFluid
          name=""
          activeKey={activeTab}
          value={activeTab}
          items={[
            {
              label: "Start Run",
              dataKey: "start_run",
            },
            {
              label: "History",
              dataKey: "runs",
              rightSlot:
                startedWorkerRunsCount > 0 ? (
                  <Chip
                    text={`${startedWorkerRunsCount}`}
                    colorVariant="dark"
                  />
                ) : null,
            },
          ]}
          onChange={(tab) => setActiveTab(tab as Tabs)}
          style={{ marginBottom: 16, width: "100%" }}
        />

        {activeTab === "start_run" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <ControlLabel label="Worker">
              <DropdownInputBare
                name="worker"
                labelKey="label"
                valueKey="value"
                placeholder="Select worker..."
                options={workerOptions}
                isSortedAlphabetically
                value={selectedWorkerId ?? ""}
                onChange={handleWorkerChange}
                isLoading={isLoadingWorkers}
                isClearable={false}
                isFluid
                noSearchDebounce
              />
            </ControlLabel>

            {selectedWorkerId != null && (
              <>
                <ControlLabel label="Version">
                  <DropdownInputBare
                    name="version"
                    labelKey="label"
                    valueKey="value"
                    placeholder="Select version..."
                    options={versionOptions}
                    isSortedAlphabetically={false}
                    value={
                      selectedVersion !== undefined
                        ? String(selectedVersion)
                        : ""
                    }
                    onChange={(val) => {
                      const raw =
                        val == null || val === "" ? undefined : (val as string);
                      const next = raw != null ? Number(raw) : undefined;
                      setSelectedVersion(
                        next !== undefined && !Number.isNaN(next)
                          ? next
                          : undefined
                      );
                    }}
                    isLoading={isLoadingVersions}
                    isDisabled={!selectedWorkerId || noVersionsToChooseFrom}
                    isFluid
                    noSearchDebounce
                  />
                </ControlLabel>

                {noVersionsToChooseFrom && (
                  <Banner
                    variant="warning"
                    titleText="No versions available"
                    description="There are no versions of this worker to run. Create a worker version first."
                  />
                )}

                {selectedVersion != null &&
                  !noVersionsToChooseFrom &&
                  (inputSchemaEntries.length === 0 ? (
                    <ControlLabel label="Input parameters">
                      <span style={{ color: "var(--color-text-secondary)" }}>
                        No input parameters for this version.
                      </span>
                    </ControlLabel>
                  ) : (
                    <Form {...runInputForm}>
                      <ControlLabel
                        label="Input parameters"
                        style={{ marginTop: 16 }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 16,
                            marginTop: 16,
                          }}
                        >
                          {inputSchemaEntries.map(([key, def]) =>
                            renderWorkerInputField({
                              schemaKey: key,
                              fieldName: key,
                              definition: def,
                            })
                          )}
                        </div>
                      </ControlLabel>
                    </Form>
                  ))}
              </>
            )}
          </div>
        )}
        {activeTab === "runs" && (
          <div>
            {queueSize === 0 && (
              <EmptyState title="No runs in this session yet." />
            )}
            {queueSize > 0 && (
              <div
                style={{
                  marginBottom: 12,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outline"
                  size="small"
                  disabled={dismissableQueuedRunIds.length === 0}
                  onClick={() => {
                    const idsToRemove = [...dismissableQueuedRunIds];
                    for (const id of idsToRemove) {
                      lastStatusByRunIdRef.current.delete(id);
                    }
                    setQueue((currentQueue) => {
                      let next = currentQueue;
                      for (const id of idsToRemove) {
                        next = next.cutById(id);
                      }
                      return next;
                    });
                  }}
                >
                  Dismiss all
                </Button>
              </div>
            )}
            {queue
              .getAsArray()
              .reverse()
              .map((queuedRun) => {
                const pollIndex = runIds.indexOf(queuedRun.id);
                const liveRun =
                  pollIndex >= 0 ? runPollQueries[pollIndex]?.data : undefined;
                const status =
                  queuedRun.notReturnedBySearchPoll === true
                    ? ("unavailable" as const)
                    : (liveRun?.status ?? WorkerRunStatus.STARTED);
                const version = liveRun?.version ?? queuedRun.version;
                const startedAt =
                  liveRun?.started_at ?? queuedRun.started_at ?? null;
                const inputRecord: Record<string, unknown> =
                  liveRun?.inputs ?? queuedRun.inputs ?? {};
                const outputsAreKnown =
                  status === WorkerRunStatus.COMPLETED ||
                  status === WorkerRunStatus.FAILED;
                const outputRecord: Record<string, unknown> =
                  liveRun?.outputs ?? {};
                const canBeDismissed =
                  status === WorkerRunStatus.COMPLETED ||
                  status === WorkerRunStatus.FAILED ||
                  status === "unavailable";
                return (
                  <ListCard
                    key={queuedRun.id}
                    renderTitle={({ FieldSegment }) => (
                      <>
                        <FieldSegment>
                          <span
                            role="presentation"
                            style={{ display: "contents", cursor: "pointer" }}
                            onClickCapture={handleClose}
                          >
                            <RoutingLink
                              to={WorkerRunDetailRoutePath.toLinkParts({
                                pathParams: { runId: queuedRun.id },
                              })}
                            >
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                {queuedRun.workerName} (Version {version})
                                <Icon
                                  variant="arrow-square-out"
                                  size={18}
                                />
                              </span>
                            </RoutingLink>
                          </span>
                        </FieldSegment>
                        <FieldSegment>
                          {status === WorkerRunStatus.STARTED && (
                            <Pill
                              variant="info"
                              iconLeft={<IconLoading />}
                            >
                              Running...
                            </Pill>
                          )}
                          {status === WorkerRunStatus.COMPLETED && (
                            <Pill
                              variant="success"
                              hasDot
                            >
                              Completed
                            </Pill>
                          )}
                          {status === WorkerRunStatus.FAILED && (
                            <Tooltip
                              content={
                                liveRun?.error_message?.trim() ?? "Run failed."
                              }
                            >
                              <Pill
                                variant="error"
                                hasDot
                              >
                                Failed
                              </Pill>
                            </Tooltip>
                          )}
                          {status === "unavailable" && (
                            <Tooltip content="This run was not returned by search. You can dismiss it or open the detail page if the run still exists.">
                              <Pill
                                variant="warning"
                                hasDot
                              >
                                Unavailable
                              </Pill>
                            </Tooltip>
                          )}
                        </FieldSegment>
                      </>
                    )}
                    renderDescription={({ FieldSegment }) => (
                      <>
                        {startedAt && (
                          <FieldSegment>
                            {startedAt && (
                              <DataTable.DataRow.DateCell
                                value={startedAt}
                                withTime
                              />
                            )}
                          </FieldSegment>
                        )}
                      </>
                    )}
                    renderMeta={({ FieldSegment }) => (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                        }}
                      >
                        <FieldSegment>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 6,
                              marginTop: 4,
                            }}
                          >
                            <div
                              style={{
                                color: "var(--color-text-secondary)",
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              Inputs
                            </div>
                            <KeyValueTags
                              record={inputRecord}
                              tagsContainerStyle={{ alignItems: "flex-start" }}
                              emptyStateStyle={{
                                color: "var(--color-text-secondary)",
                              }}
                            />
                          </div>
                        </FieldSegment>
                        <FieldSegment>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 6,
                              marginTop: 4,
                            }}
                          >
                            <div
                              style={{
                                color: "var(--color-text-secondary)",
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              Outputs
                            </div>
                            {outputsAreKnown ? (
                              <KeyValueTags
                                record={outputRecord}
                                tagsContainerStyle={{
                                  alignItems: "flex-start",
                                }}
                                emptyStateStyle={{
                                  color: "var(--color-text-secondary)",
                                }}
                              />
                            ) : status === "unavailable" ? (
                              <span
                                style={{
                                  color: "var(--color-text-secondary)",
                                }}
                              >
                                Status could not be loaded from search.
                              </span>
                            ) : (
                              <span
                                style={{
                                  color: "var(--color-text-secondary)",
                                }}
                              >
                                Not available yet
                              </span>
                            )}
                          </div>
                        </FieldSegment>
                      </div>
                    )}
                    actions={
                      canBeDismissed
                        ? [
                            {
                              label: "Dismiss",
                              onClick: () => {
                                lastStatusByRunIdRef.current.delete(
                                  queuedRun.id
                                );
                                setQueue((currentQueue) =>
                                  currentQueue.cutById(queuedRun.id)
                                );
                              },
                              slotLeft: ({ iconProps }) => (
                                <Icon
                                  variant="x-circle"
                                  {...iconProps}
                                />
                              ),
                            },
                          ]
                        : undefined
                    }
                  />
                );
              })}
          </div>
        )}
      </Drawer>
    </IsFeatureAvailable>
  );
};
